import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const MONTH_LABELS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isBetween = (d, start, end) => d >= start && d <= end;

/**
 * RentalMiniCalendar — visual month calendar that shows reserved days for a rental product.
 *
 * Props:
 *  - productId: string (required)
 *  - selectedStart / selectedEnd: 'YYYY-MM-DD' strings (highlighted as the user's pending selection)
 *  - onDateClick: (isoDateString) => void  — clicking a free day calls this; reserved days are not clickable
 */
export const RentalMiniCalendar = ({ productId, selectedStart, selectedEnd, onDateClick }) => {
  const now = new Date();
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchMonth = useCallback(async (m, y) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/rentals/calendar/${productId}`, {
        params: { month: m + 1, year: y },
      });
      setReservations(res.data.reservations || []);
    } catch {
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchMonth(viewMonth, viewYear);
  }, [viewMonth, viewYear, fetchMonth]);

  // Build a set of reserved date strings for fast lookup
  const reservedDays = useMemo(() => {
    const set = new Set();
    reservations.forEach((r) => {
      const s = new Date(r.start_date);
      const e = new Date(r.end_date);
      const cursor = new Date(s.getFullYear(), s.getMonth(), s.getDate());
      const stop = new Date(e.getFullYear(), e.getMonth(), e.getDate());
      while (cursor <= stop) {
        set.add(toISODate(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return set;
  }, [reservations]);

  // Calendar grid
  const firstDay = new Date(viewYear, viewMonth, 1);
  const startOffset = firstDay.getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const today0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const selStart = selectedStart ? new Date(selectedStart + 'T00:00:00') : null;
  const selEnd = selectedEnd ? new Date(selectedEnd + 'T00:00:00') : null;

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const goPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const goNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const canGoPrev = viewYear > now.getFullYear() || (viewYear === now.getFullYear() && viewMonth > now.getMonth());

  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid="rental-mini-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <button
          type="button"
          onClick={goPrev}
          disabled={!canGoPrev}
          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mois précédent"
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="h-4 w-4 text-black" />
        </button>
        <p className="text-sm font-semibold text-black flex items-center gap-2">
          {MONTH_LABELS[viewMonth]} {viewYear}
          {loading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
        </p>
        <button
          type="button"
          onClick={goNext}
          className="p-1 rounded hover:bg-gray-100"
          aria-label="Mois suivant"
          data-testid="calendar-next-month"
        >
          <ChevronRight className="h-4 w-4 text-black" />
        </button>
      </div>

      {/* Day labels */}
      <div className="grid grid-cols-7 px-2 pt-2 text-center text-[10px] text-gray-400 uppercase">
        {DAY_LABELS.map((lbl, idx) => (
          <div key={idx}>{lbl}</div>
        ))}
      </div>

      {/* Cells */}
      <div className="grid grid-cols-7 gap-1 p-2">
        {cells.map((d, idx) => {
          if (!d) return <div key={idx} className="h-8" />;
          const iso = toISODate(d);
          const isPast = d < today0;
          const isReserved = reservedDays.has(iso);
          const isToday = sameDay(d, now);
          const isSelStart = selStart && sameDay(d, selStart);
          const isSelEnd = selEnd && sameDay(d, selEnd);
          const inSelRange = selStart && selEnd && isBetween(d, selStart, selEnd);

          let cls = 'h-8 rounded text-xs flex items-center justify-center transition-colors relative ';
          let testid = `cal-day-${iso}`;
          const disabled = isPast || isReserved;

          if (isReserved) {
            cls += 'bg-red-100 text-red-700 line-through cursor-not-allowed';
          } else if (isPast) {
            cls += 'text-gray-300 cursor-not-allowed';
          } else if (isSelStart || isSelEnd) {
            cls += 'bg-black text-white font-semibold cursor-pointer';
          } else if (inSelRange) {
            cls += 'bg-gray-200 text-black cursor-pointer';
          } else {
            cls += 'text-black hover:bg-gray-100 cursor-pointer';
            if (isToday) cls += ' ring-1 ring-black';
          }

          return (
            <button
              key={idx}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onDateClick && onDateClick(iso)}
              className={cls}
              data-testid={testid}
              aria-label={`${d.getDate()} ${MONTH_LABELS[viewMonth]}${isReserved ? ' réservé' : ''}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="px-3 pb-3 pt-1 flex items-center gap-3 text-[10px] text-gray-500 border-t border-gray-100">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-black inline-block" />Sélectionné
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-gray-200 inline-block" />Plage
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-sm bg-red-100 border border-red-200 inline-block" />Réservé
        </span>
      </div>
    </div>
  );
};

export default RentalMiniCalendar;
